import React from "react";
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';

import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import stc from "string-to-color";


export default function UserToken({user, disconnect, pendingCount = 0, isAdmin = false}) {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const navigate = useNavigate();

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    function getInitial() {
        return user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase();
    }

    function getFullName() {
        return `${user.firstName} ${user.lastName}`;
    }

    if (!user) {
        return (
            <Tooltip title={t("login.login")} arrow>
                <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={() => navigate("/private/login")}
                    color="inherit"
                    sx={{
                        transition: "background 0.2s",
                        "&:hover": {bgcolor: "rgba(255,255,255,0.15)"},
                    }}
                >
                    <AccountCircle/>
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <>
            <Tooltip title={getFullName()} arrow>
                <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                    sx={{p: 0.5}}
                >

                        <Avatar
                            sx={{
                                bgcolor: stc(getInitial()),
                                width: 36,
                                height: 36,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                transition: "box-shadow 0.2s, transform 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                                    transform: "scale(1.08)",
                                },
                            }}
                        >
                            {getInitial()}
                        </Avatar>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                elevation={4}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 200,
                            mt: 1,
                            borderRadius: 2,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                {/* Header utilisateur */}
                <Box sx={{px: 2, py: 1.5, bgcolor: "action.hover"}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                        <Avatar
                            sx={{
                                bgcolor: stc(getInitial()),
                                width: 40,
                                height: 40,
                                fontSize: "0.9rem",
                                fontWeight: 700,
                            }}
                        >
                            {getInitial()}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                                {getFullName()}
                            </Typography>
                            {user.email && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {user.email}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Divider/>

                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        navigate("/private/info");
                    }}
                    sx={{gap: 1.5, py: 1.2, "&:hover": {bgcolor: "action.hover"}}}
                >
                    <ManageAccountsIcon fontSize="small" color="action"/>
                    <Typography variant="body2">{t("login.account")}</Typography>
                </MenuItem>

                <Divider sx={{my: 0.5}}/>

                <MenuItem
                    onClick={() => {
                        disconnect();
                        setAnchorEl(null);
                    }}
                    sx={{gap: 1.5, py: 1.2, color: "error.main", "&:hover": {bgcolor: "error.lighter", color: "error.dark"}}}
                >
                    <LogoutIcon fontSize="small"/>
                    <Typography variant="body2" fontWeight={500}>{t("login.logout")}</Typography>
                </MenuItem>
            </Menu>
        </>
    );
}
